import { useSyncExternalStore } from 'react';
import {
  readAutoOpened,
  readDismissed,
  writeAutoOpened,
  writeDismissed,
} from './whats-new.util';

/**
 * The card's UI state, shared by the card and the sidebar's What's new button.
 * `open` is the card being up; `nonce` changes on each open so the card
 * replays its slide-in; `start` is the announcement it opens at.
 */
export interface WhatsNewUi {
  open: boolean;
  nonce: number;
  start: number;
}

let ui: WhatsNewUi = { open: false, nonce: 0, start: 0 };
// Bumped whenever the seen ids change, so every reader re-reads them.
let seenVersion = 0;
// Seen ids for this tab, so a closed card stays closed when storage is blocked.
const memorySeen = new Map<string, Set<string>>();
// Users the card already opened for by itself since this page loaded.
const autoOpened = new Set<string>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function publish(next: WhatsNewUi): void {
  ui = next;
  notify();
}

export function openWhatsNew(start = 0): void {
  publish({ ...ui, open: true, nonce: ui.nonce + 1, start });
}

export function closeWhatsNew(): void {
  if (ui.open) publish({ ...ui, open: false });
}

/**
 * Opens the card without a click, at the announcement `start`, once per
 * unseen announcement for this user on this browser: a reload or a later
 * sign-in leaves it shut until something new arrives. Opening marks nothing
 * seen (closing does), so the box keeps saying it is unread. The one open per
 * load is what keeps it from reopening on someone who just closed it.
 */
export function autoOpenWhatsNew(
  userId: string,
  start: number,
  unseenIds: string[]
): void {
  if (autoOpened.has(userId)) return;
  autoOpened.add(userId);
  const opened = readAutoOpened(userId);
  const fresh = unseenIds.filter((id) => !opened.includes(id));
  if (!fresh.length) return;
  writeAutoOpened(userId, fresh);
  if (!ui.open) openWhatsNew(start);
}

/** Announcement ids this user has seen: stored, or seen in this tab. */
function readSeen(userId: string | null): string[] {
  if (!userId) return [];
  const seen = new Set(readDismissed(userId));
  for (const id of memorySeen.get(userId) ?? []) seen.add(id);
  return [...seen];
}

export function markSeen(userId: string, ids: string[]): void {
  if (!ids.length) return;
  writeDismissed(userId, ids);
  const seen = memorySeen.get(userId) ?? new Set<string>();
  for (const id of ids) seen.add(id);
  memorySeen.set(userId, seen);
  seenVersion++;
  notify();
}

/**
 * Tests reset the module state with this. It is what a page load starts from:
 * the memory is gone and the stored seen ids are not.
 */
export function resetWhatsNewStore(): void {
  ui = { open: false, nonce: 0, start: 0 };
  memorySeen.clear();
  autoOpened.clear();
  seenVersion++;
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const readUi = () => ui;

export function useWhatsNewUi(): WhatsNewUi {
  return useSyncExternalStore(subscribe, readUi, readUi);
}

// One array per (user, change), so a render that changed nothing gets the
// same array back, as useSyncExternalStore requires.
let seenSnapshot: { userId: string | null; version: number; ids: string[] } = {
  userId: null,
  version: -1,
  ids: [],
};

function readSeenSnapshot(userId: string | null): string[] {
  if (seenSnapshot.userId !== userId || seenSnapshot.version !== seenVersion)
    seenSnapshot = { userId, version: seenVersion, ids: readSeen(userId) };
  return seenSnapshot.ids;
}

/**
 * The ids this user has seen, re-read whenever they change. Read through the
 * store rather than straight from storage in render: the React Compiler
 * caches a plain call on its arguments, so a direct read never saw a change.
 */
export function useSeenIds(userId: string | null): string[] {
  const read = () => readSeenSnapshot(userId);
  return useSyncExternalStore(subscribe, read, read);
}
